import React, { useMemo, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
    FiMail,
    FiSend,
    FiX,
    FiFileText,
    FiCheck,
    FiPaperclip,
    FiUsers,
    FiSearch,
    FiEye,
    FiExternalLink,
} from "react-icons/fi";
import { Loader } from "lucide-react";
import { supabase } from "../../config/supabase";
import Toast from "../common/Toast";
import DropdownSection from "../common/DropdownSection";
import { uuidToShortId } from "../../lib/common/shortId";
import TextTooltip from "./TextTooltip";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FILE_TYPE_LABELS = {
    initial: "Job Files",
    design: "Design Files",
    estimation: "Estimation Files",
    delivery: "Delivery Files",
    timber: "Timber Files",
    custom: "Custom Files",
    other: "Other Files",
};



const JobBookedDatesEmailSender = ({ assignment, userProfile, company }) => {
    const [open, setOpen] = useState(false);
    const [userPickerOpen, setUserPickerOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [sending, setSending] = useState(false);

    const [ccInput, setCcInput] = useState("");
    const [ccEmails, setCcEmails] = useState([]);
    const [users, setUsers] = useState([]);
    const [userSearch, setUserSearch] = useState("");
    const [pickerSelectedIds, setPickerSelectedIds] = useState([]);

    const [previewOpen, setPreviewOpen] = useState(false);
    const defaultCcAppliedRef = useRef(false);

    const permissions = userProfile?.permissions || userProfile?.permission || [];
    const canSendFiles = permissions.includes("SEND_FILES_EMAIL");

    const [selectedDateKeys, setSelectedDateKeys] = useState([]);
    const [sentDateKeys, setSentDateKeys] = useState(new Set());

    const [form, setForm] = useState({
        to: "",
        subject: "",
        content: "",
    });

    const defaultCcUserIds = useMemo(() => {
        const settings = company?.settings || {};

        return (
            settings.booked_dates_default_cc_user_ids ||
            settings.default_cc_user_ids ||
            []
        );
    }, [company?.settings]);

    const isJobCancelled = !!assignment?.is_cancelled;

    const CancelledAction = ({ children, allow = false }) => {
        if (!isJobCancelled || allow) return children;

        return (
            <TextTooltip text="This job is cancelled">
                <span className="inline-flex cursor-not-allowed">
                    {React.cloneElement(children, {
                        disabled: true,
                        onClick: undefined,
                        className: `${children.props.className || ''} opacity-50 cursor-not-allowed pointer-events-none`
                    })}
                </span>
            </TextTooltip>
        );
    };


    useEffect(() => {
        if (!open) return;

        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, [open]);

    useEffect(() => {
        if ((!userPickerOpen && defaultCcUserIds.length === 0) || !company?.id) return;

        supabase
            .from("users")
            .select("id, full_name, username, email, role, avatar_url")
            .eq("company_id", company.id)
            .order("full_name", { ascending: true })
            .then(({ data, error }) => {
                if (error) {
                    console.error("Error loading users:", error);
                    Toast.error("Failed to load users");
                    return;
                }

                setUsers((data || []).filter(u => !!u.email));
            });
    }, [userPickerOpen, company?.id, defaultCcUserIds]);

    useEffect(() => {
        if (!open || defaultCcAppliedRef.current) return;
        if (!users.length || !defaultCcUserIds.length) return;

        const defaultEmails = users
            .filter(user => defaultCcUserIds.includes(user.id))
            .map(user => user.email)
            .filter(Boolean);

        setCcEmails(defaultEmails.map(email => email.toLowerCase()));

        defaultCcAppliedRef.current = true;
    }, [open, users, defaultCcUserIds]);


    const jobShortId = useMemo(() => {
        try {
            return uuidToShortId(assignment?.id);
        } catch {
            return assignment?.short_id || assignment?.id;
        }
    }, [assignment?.id, assignment?.short_id]);

    const contactUrl = `https://app.notifyapp.com.au/contact/${jobShortId}`;

    const logoUrl = useMemo(() => {
        const isEnterprise = company?.subscription_plan === "enterprise";
        return isEnterprise && company?.enterprise_logo_url
            ? company.enterprise_logo_url
            : "https://xgezccwtibbfoplffzcr.supabase.co/storage/v1/object/public/public-images/notify/wide-logo-black.svg";
    }, [company?.subscription_plan, company?.enterprise_logo_url]);

    const bookedDates = useMemo(() => {
        const TYPE_ORDER = {
            wall: 0,
            floor: 1,
            roof: 2,
            timber: 3,
        };

        const getChildSortGroup = (childId = "") => {
            const value = String(childId || "").toUpperCase();

            // First: IDs that do not contain L
            if (value && !value.includes("L")) return 0;

            // Second: IDs that contain L
            if (value.includes("L")) return 1;

            // Last: empty / others
            return 2;
        };

        const compareChildIds = (a = "", b = "") => {
            const valueA = String(a || "").toUpperCase();
            const valueB = String(b || "").toUpperCase();

            const groupA = getChildSortGroup(valueA);
            const groupB = getChildSortGroup(valueB);

            if (groupA !== groupB) {
                return groupA - groupB;
            }

            return valueA.localeCompare(valueB, undefined, {
                numeric: true,
                sensitivity: "base",
            });
        };

        return (assignment?.job_quotes || [])
            .filter(q => q.is_required && q.expected_delivery_date)
            .map(q => ({
                quote_type: q.quote_type,
                child_unique_id: q.child_unique_id || q.quote_type,
                date: q.expected_delivery_date,
                time: q?.delivery_time_slot || "-"
            }))
            .sort((a, b) => {
                const typeA = TYPE_ORDER[a.quote_type?.toLowerCase()] ?? 999;
                const typeB = TYPE_ORDER[b.quote_type?.toLowerCase()] ?? 999;

                if (typeA !== typeB) {
                    return typeA - typeB;
                }

                return compareChildIds(a.child_unique_id, b.child_unique_id);
            });
    }, [assignment?.job_quotes]);

    const getBookedDateKey = (row) => {
        return [
            row.quote_type || "",
            row.child_unique_id || "",
            row.date || "",
            row.time || "",
        ].join("|");
    };

    const selectedBookedDates = useMemo(() => {
        return bookedDates.filter(row => selectedDateKeys.includes(getBookedDateKey(row)));
    }, [bookedDates, selectedDateKeys]);

    const allBookedDateKeys = useMemo(() => {
        return bookedDates.map(getBookedDateKey);
    }, [bookedDates]);

    const allDatesSelected =
        allBookedDateKeys.length > 0 &&
        selectedDateKeys.length === allBookedDateKeys.length;

    const toggleBookedDate = (row) => {
        const key = getBookedDateKey(row);

        setSelectedDateKeys(prev =>
            prev.includes(key)
                ? prev.filter(item => item !== key)
                : [...prev, key]
        );
    };

    const selectAllDates = () => {
        setSelectedDateKeys(allBookedDateKeys);
    };

    const clearAllDates = () => {
        setSelectedDateKeys([]);
    };

    useEffect(() => {
        if (!open || !assignment?.id || !company?.id) return;

        let active = true;

        const loadSentBookedDates = async () => {
            const { data, error } = await supabase
                .from("customer_conversations")
                .select("metadata")
                .eq("company_id", company.id)
                .eq("job_id", assignment.id)
                .eq("conversation_type", "booked_dates_email")
                .eq("direction", "outbound")
                .eq("status", "sent");

            if (error) {
                console.error("Failed to load sent booked dates:", error);
                return;
            }

            if (!active) return;

            const sentKeys = new Set();

            for (const row of data || []) {
                const dates = row?.metadata?.booked_dates || [];

                if (Array.isArray(dates)) {
                    dates.forEach(dateRow => {
                        sentKeys.add(getBookedDateKey(dateRow));
                    });
                }
            }

            setSentDateKeys(sentKeys);
        };

        loadSentBookedDates();

        return () => {
            active = false;
        };
    }, [open, assignment?.id, company?.id]);


    if (!canSendFiles) return null;

    const openModal = () => {
        setSelectedIds([]);
        setSelectedDateKeys(bookedDates.map(getBookedDateKey));
        setSentDateKeys(new Set());

        setCcEmails([]);
        setCcInput("");
        setPickerSelectedIds([]);
        setForm({
            to: assignment?.customer_mail || assignment?.customer_email || "",
            subject: `Booked Dates - ${assignment?.unique_id || ""} - ${assignment?.customer_address || ""}`,
            content: `Hi ${assignment?.customer_name || ""},\n\nPlease find the booked dates for job below:\n${assignment?.unique_id || ""} - ${assignment?.customer_address || ""}`,
        });
        defaultCcAppliedRef.current = false;
        setOpen(true);
    };

    const addCcEmail = (email) => {
        const clean = String(email || "").trim().replace(/,$/, "").toLowerCase();
        if (!clean) return;

        if (!EMAIL_RE.test(clean)) {
            Toast.error(`Invalid email: ${clean}`);
            return;
        }

        setCcEmails(prev => {
            if (prev.includes(clean)) return prev;
            return [...prev, clean];
        });
    };

    const removeCcEmail = (email) => {
        setCcEmails(prev => prev.filter(item => item !== email));
    };

    const handleCcKeyDown = (e) => {
        if (["Enter", "Tab", ","].includes(e.key)) {
            e.preventDefault();
            addCcEmail(ccInput);
            setCcInput("");
        }

        if (e.key === "Backspace" && !ccInput && ccEmails.length > 0) {
            setCcEmails(prev => prev.slice(0, -1));
        }
    };

    const toggleFile = (fileId) => {
        setSelectedIds(prev =>
            prev.includes(fileId)
                ? prev.filter(id => id !== fileId)
                : [...prev, fileId]
        );
    };

    const toggleUserPicker = (userId) => {
        setPickerSelectedIds(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const applySelectedUsersToCc = () => {
        users
            .filter(user => pickerSelectedIds.includes(user.id))
            .forEach(user => addCcEmail(user.email));

        setUserPickerOpen(false);
    };

    const filteredUsers = users.filter(user => {
        const q = userSearch.toLowerCase();
        return (
            user.full_name?.toLowerCase().includes(q) ||
            user.username?.toLowerCase().includes(q) ||
            user.email?.toLowerCase().includes(q) ||
            user.role?.toLowerCase().includes(q)
        );
    });


    const buildEmailHtml = () => {
        const rawContent = form.content || "";

        const signatureMatch = rawContent.match(/\n\nKind regards,[\s\S]*/i);

        const mainContent = signatureMatch
            ? rawContent.replace(signatureMatch[0], "")
            : rawContent;

        const signatureContent = signatureMatch
            ? signatureMatch[0].trim()
            : "";

        const safeMainContent = mainContent
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/\n/g, "<br />");

        const safeSignature = signatureContent
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/\n/g, "<br />");

        const bookedDatesHtml = selectedBookedDates.map(row => `
            <tr>
                <td style="padding:12px;border-bottom:1px solid #eef0f3;font-size:13px;color:#111827;font-weight:700;font-family:'Poppins',monospace;font-variant-numeric:tabular-nums;white-space:nowrap;text-transform:capitalize;">
                    ${row.quote_type}
                </td>
                <td style="padding:12px;border-bottom:1px solid #eef0f3;font-size:13px;color:#4b5563;text-transform:uppercase;">
                    ${row.child_unique_id}
                </td>
               <td style="padding:12px;border-bottom:1px solid #eef0f3;font-size:13px;color:#111827;font-weight:700;font-family:'Courier New',monospace;font-variant-numeric:tabular-nums;white-space:nowrap;">
    <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
        <tr>
            <td style="padding:0;margin:0;font-size:13px;color:#111827;font-weight:700;font-family:'Courier New',monospace;line-height:1.3;white-space:nowrap;">
                ${formatBookedDate(row.date)}
            </td>
        </tr>
        <tr>
            <td style="padding:0;margin:0;font-family:Poppins,Helvetica,sans-serif;font-weight:600;font-size:12px;color:#636363;line-height:1.3;white-space:nowrap;">
                ${row?.time || "-"}
            </td>
        </tr>
    </table>
</td>
            </tr>
        `).join("");

        const companyName = company?.name || "Notify";
        const companyAddress = [
            company?.address,
            company?.state,
            company?.postal_code
        ]
            .filter(Boolean)
            .join(" ")
            .trim();


        //  <div style="margin-top:28px;background:#f9fafb;border:1px solid #eef0f3;border-radius:14px;padding:16px 18px;">
        //                     <p style="margin:0 0 8px;font-size:13px;font-weight:700;color:#111827;">
        //                         Attached Files
        //                     </p>
        //                     <div style="margin-top:10px;">
        //                         ${filesHtml || `<p style="font-size:13px;color:#667085;margin:0;">No files selected</p>`}
        //                     </div>
        //                 </div>

        // <div style="margin:30px 0 8px;padding:20px;background:#f9fafb;border:1px solid #eef0f3;border-radius:16px;text-align:left;">
        //     <p style="margin:0 0 6px;font-size:15px;font-weight:700;color:#111827;">
        //         Need to share files or send us a message?
        //     </p>
        //     <p style="margin:0 0 16px;font-size:13px;line-height:1.6;color:#667085;">
        //         You can use the contact page below to upload files, provide updates, or send any job-related information directly to our team.
        //     </p>
        //     <a href="${contactUrl}" style="display:inline-block;background:#111827;color:#ffffff;text-decoration:none;padding:12px 20px;border-radius:12px;font-size:14px;font-weight:700;">
        //         Contact Us
        //     </a>
        // </div>


        //  ${
        //     (companyAddress || company?.website) ? `
        //                                 <p style="font-size:14px;line-height:1.8;color:#374151;margin-top:0;">
        //                                     ${companyAddress ? `<span style="font-weight:600;">A:</span> ${companyAddress}` : ""}
        //                                     ${companyAddress && company?.website ? ` | ` : ""}
        //                                     ${company?.website ? `<span style="font-weight:600;">W:</span> <a href=\`https://${company?.website}\` target="_blank">${company.website}</a>` : ""}
        //                                 </p>
        //                             ` : ""
        // }
        // <img
        //     src=${logoUrl}
        //     alt="${company?.name}"
        //     style="display:block;height:auto;max-width:70%;width:auto;object-fit:contain;margin:0;border:0;outline:none;text-decoration:none;color:#111827 !important;"
        // />

        return `
        <html>
        <head>
            <meta name="color-scheme" content="light dark">
            <meta name="supported-color-schemes" content="light dark">
            <style>
                :root {
                    color-scheme: light only;
                    supported-color-schemes: light only;
                }

                body {
                    margin: 0 !important;
                    padding: 0 !important;
                    background: #f4f5f7 !important;
                    color: #111827 !important;
                }

                @media (prefers-color-scheme: dark) {
                    body,
                    .force-page {
                        background: #f4f5f7 !important;
                        color: #111827 !important;
                    }

                    .force-card,
                    .force-white {
                        background: #ffffff !important;
                        color: #111827 !important;
                    }

                    .force-muted {
                        color: #4b5563 !important;
                    }
                }
            </style>
        </head>

        <body style="margin:0;padding:0;background:#f4f5f7 !important;color:#111827 !important;">
            <div class="force-page" style="margin:0;padding:0;background:#f4f5f7 !important;font-family:Poppins,Helvetica,sans-serif;color:#111827 !important;">

                <div style="max-width:680px;margin:0 auto;padding:28px 16px;">
                <div class="force-card" style="background:#ffffff !important;color:#111827 !important;border-radius:18px;overflow:hidden;border:1px solid #e5e7eb;">
                       <div class="force-white" style="padding:26px 28px;text-align:center;border-bottom:1px solid #eef0f3;background:#ffffff !important;color:#111827 !important;">
                            <div style="min-height:86px;text-align:center;background:#ffffff !important;color:#111827 !important;">
                              
                                                   
    <img 
        src="https://xgezccwtibbfoplffzcr.supabase.co/storage/v1/object/public/public-images/notify/wide-logo-black.svg" 
        alt="Notify"
        width="160"
        style="display:block;height:auto;max-height:80px;max-width:180px;width:auto;object-fit:contain;margin:0 0 auto;border:0;outline:none;text-decoration:none;color:#111827 !important;"
    />

                                <div style="text-align:start;font-size:14px;font-weight:700;color:#111827 !important;margin-top:10px;font-family:Poppins,Helvetica,sans-serif;">
                                    Notify <span style="font-weight:500;color:#27272a;letter-spacing:0.4px;">
    - BUILT AROUND YOUR BUSINESS
</span>
                                </div>
                            </div>
                        </div>

<div class="force-white" style="padding:30px 28px;background:#ffffff !important;color:#111827 !important;">
                            <p style="font-size:15px;line-height:1.7;margin:0 0 22px;color:#374151;">
                                ${safeMainContent}
                            </p>

                            <table style="width:100%;border-collapse:collapse;background:#ffffff;border:1px solid #eef0f3;border-radius:12px;overflow:hidden;">
                                <thead>
                                    <tr style="background:#f3f4f6;">
                                        <th style="padding:12px;text-align:left;font-size:11px;color:#6b7280;letter-spacing:0.3px;">JOB TYPE</th>
                                        <th style="padding:12px;text-align:left;font-size:11px;color:#6b7280;letter-spacing:0.3px;">JOB ID</th>
                                        <th style="padding:12px;text-align:left;font-size:11px;color:#6b7280;letter-spacing:0.3px;">DATE & TIME</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    ${bookedDatesHtml || `
                                        <tr>
                                            <td colspan="3" style="padding:14px;font-size:13px;color:#667085;">
                                                No booked dates found.
                                            </td>
                                        </tr>
                                    `}
                                </tbody>
                            </table>
                        
                             <div style="margin:30px 0 8px;padding:20px;background:#f9fafb;border:1px solid #eef0f3;border-radius:16px;text-align:left;">
            <p style="margin:0 0 6px;font-size:15px;font-weight:700;color:#111827;">
                Need to share files or send us a message?
            </p>
            <p style="margin:0;font-size:13px;line-height:1.6;color:#667085;white-space:pre-line;">You can reply directly to this email with updates, questions, or important job details including file attachments such as plans, photos, or documents.\n\nOur team will receive everything instantly.</p>
        </div>

                                <div style="margin-top:4rem;padding-top:18px;border-top:1px solid #eef0f3;">
                                    <p style="font-size:14px;line-height:1.8;margin:0;color:#374151;">
                                        Kind regards,
                                    </p>
                                </div>
                            

                             <div style="margin-top:1rem;padding-top:18px;">
                                    <p style="font-size:14px;font-weight:600;line-height:1.8;margin:0;color:#374151;">
                                        ${userProfile.full_name || userProfile.username}
                                    </p>
                                    ${userProfile?.position ? `
                                    <p style="font-size:14px;line-height:1.8;margin:0;color:#374151;">
                                        ${userProfile.position}
                                    </p>` : ``
            }
                                     <p style="font-size:14px;line-height:1.8;margin:0;color:#374151;margin-top:2rem;">
                                        <span style="font-weight:500;">T:</span> ${company?.phone} | <span style="font-weight:500;">E:</span> ${userProfile?.email}
                                    </p>
                                  


                                   
                                </div>
                        
                        </div>

                    <div style="padding:26px 28px;background:#f9fafb;border-top:1px solid #eef0f3;text-align:left;">

                            
                            <div style="margin:0 0 14px;">

                                <p style="margin:0 0 8px;font-size:13px;color:#4b5563;line-height:1.6;">
                                    📧 
                                    <a 
                                        href="mailto:info@notifyapp.com.au"
                                        style="color:#111827;text-decoration:none;font-weight:600;"
                                    >
                                        info@notifyapp.com.au
                                    </a>
                                </p>

                                <p style="margin:0;font-size:13px;color:#4b5563;line-height:1.6;">
                                    🌐 
                                    <a 
                                        href="https://notifyapp.com.au"
                                        style="color:#111827;text-decoration:none;font-weight:600;"
                                    >
                                        notifyapp.com.au
                                    </a>
                                </p>

                            </div>

                            <div style="height:1px;background:#dfe3e8;margin:16px 0;"></div>

                            <p style="
                                margin:0;
                                font-size:11px;
                                color:#6b7280;
                                line-height:1.6;
                            ">
                                © ${new Date().getFullYear()} Notify. All rights reserved.
                            </p>

                        </div>
                    </div>
                </div>
            </div>
        </body>
    </html>
    `;
    };

    const handleSend = async () => {
        if (!form.to.trim()) {
            Toast.error("Please enter recipient email");
            return;
        }

        if (!form.subject.trim()) {
            Toast.error("Please enter subject");
            return;
        }

        if (ccInput.trim()) {
            addCcEmail(ccInput);
            setCcInput("");
        }

        if (selectedBookedDates.length === 0) {
            Toast.error("Please select at least one booked date");
            return;
        }

        setSending(true);

        try {
            const { data, error } = await supabase.functions.invoke("send-booked-dates-email-postmark", {
                body: {
                    jobId: assignment.id,
                    shortenedJobId: jobShortId,
                    companyId: company.id,
                    to: form.to.trim(),
                    cc: ccEmails,
                    subject: form.subject.trim(),
                    content: form.content,
                    html: buildEmailHtml(),
                    contactUrl,
                    logoUrl,
                    bookedDates: selectedBookedDates,
                    companyName: company?.name || null
                },
            });

            if (error) throw error;
            if (data?.error) throw new Error(data.error);

            Toast.success("Email sent successfully", {
                position: "bottom-right",
                autoClose: 2500,
            });

            setOpen(false);
        } catch (err) {
            console.error("Error sending files email:", err);
            Toast.error(err.message || "Failed to send email", {
                position: "bottom-right",
                autoClose: 4000,
            });
        } finally {
            setSending(false);
        }
    };



    const formatBookedDate = (date) => {
        if (!date) return "-";
        return new Date(date).toLocaleDateString("en-AU", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const unselectSentDates = () => {
        setSelectedDateKeys(prev =>
            prev.filter(key => !sentDateKeys.has(key))
        );
    };

    const modal = open ? (
        <div className="mobile-email-modal fixed inset-0 z-[9999] bg-black/45 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-5">
            <div className="bg-white w-full sm:max-w-6xl h-[100dvh] sm:h-[88vh] rounded-none sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col">

                <div className="px-4 sm:px-7 py-4 sm:py-5 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white flex-shrink-0">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-2xl bg-gray-900 text-white flex items-center justify-center shadow-sm flex-shrink-0">
                                <FiMail className="w-5 h-5" />
                            </div>

                            <div className="min-w-0">
                                <h3 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                                    Send Job Files{" "}
                                    <span className="px-2 py-0.5 ml-1 bg-zinc-200 rounded-lg text-sm">
                                        {assignment?.unique_id}
                                    </span>
                                </h3>
                                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                                    Compose an email and send booked dates.
                                </p>

                                <div className="flex flex-wrap items-center gap-2 mt-2 sm:mt-3">
                                    <span className="px-2.5 py-1 rounded-full bg-white border border-gray-200 text-xs font-medium text-gray-600">
                                        {bookedDates.length} Booked Dates
                                    </span>
                                </div>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => !sending && setOpen(false)}
                            disabled={sending}
                            className="w-10 h-10 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
                        >
                            <FiX className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                <div className="mobile-email-body flex-1 min-h-0 p-3 sm:p-6 bg-gray-50 overflow-hidden">
                    <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-3 sm:gap-6 h-full min-h-0">

                        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex flex-col min-h-0">
                            <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100 flex-shrink-0">
                                <h4 className="text-sm font-bold text-gray-900">Email Content</h4>
                                <p className="text-xs text-gray-500 mt-1">
                                    Recipient, CC, subject and message.
                                </p>
                            </div>

                            <div className="p-4 sm:p-5 space-y-4 overflow-y-auto min-h-0">
                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1.5">
                                        Send to
                                    </label>
                                    <input
                                        type="email"
                                        value={form.to}
                                        onChange={e => setForm(prev => ({ ...prev, to: e.target.value }))}
                                        className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-3 text-sm outline-none focus:bg-white focus:border-gray-900 focus:ring-4 focus:ring-gray-900/5 transition-all"
                                        placeholder="customer@email.com"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1.5">
                                        CC
                                    </label>

                                    <div className="min-h-[48px] w-full border border-gray-200 bg-gray-50 rounded-xl px-2.5 py-2 flex items-center gap-2 flex-wrap focus-within:bg-white focus-within:border-gray-900 focus-within:ring-4 focus-within:ring-gray-900/5 transition-all">
                                        {ccEmails.map(email => (
                                            <span
                                                key={email}
                                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-gray-200 text-xs font-medium text-gray-700 shadow-sm max-w-full"
                                            >
                                                <span className="truncate max-w-[190px]">{email}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeCcEmail(email)}
                                                    className="text-gray-400 hover:text-red-500"
                                                >
                                                    <FiX className="w-3.5 h-3.5" />
                                                </button>
                                            </span>
                                        ))}

                                        <input
                                            value={ccInput}
                                            onChange={e => setCcInput(e.target.value)}
                                            onKeyDown={handleCcKeyDown}
                                            onBlur={() => {
                                                if (ccInput.trim()) {
                                                    addCcEmail(ccInput);
                                                    setCcInput("");
                                                }
                                            }}
                                            className="flex-1 min-w-[140px] bg-transparent outline-none text-sm py-1.5"
                                            placeholder={ccEmails.length ? "" : "Add CC emails..."}
                                        />

                                        <button
                                            type="button"
                                            onMouseDown={e => e.preventDefault()}
                                            onClick={() => setUserPickerOpen(true)}
                                            className="ml-auto px-2.5 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-semibold hover:bg-black flex items-center gap-1.5"
                                        >
                                            <FiUsers className="w-3.5 h-3.5" />
                                            Select Users
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-600 mb-1.5">
                                        Subject
                                    </label>
                                    <input
                                        value={form.subject}
                                        onChange={e => setForm(prev => ({ ...prev, subject: e.target.value }))}
                                        className="w-full border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-3 text-sm outline-none focus:bg-white focus:border-gray-900 focus:ring-4 focus:ring-gray-900/5 transition-all"
                                    />
                                </div>

                                <div className="relative">
                                    <label className="block text-xs font-bold text-gray-600 mb-1.5">
                                        Message
                                    </label>
                                    <textarea
                                        value={form.content}
                                        onChange={e => setForm(prev => ({ ...prev, content: e.target.value }))}
                                        rows={8}
                                        className="w-full min-h-[190px] sm:min-h-[240px] border border-gray-200 bg-gray-50 rounded-xl px-3.5 py-3 text-sm outline-none focus:bg-white focus:border-gray-900 focus:ring-4 focus:ring-gray-900/5 transition-all resize-none leading-6"
                                    />
                                    <div className="pointer-events-none absolute bottom-3 right-3 max-w-[240px] rounded-xl border border-blue-100 bg-blue-50/95 px-3 py-2 shadow-sm backdrop-blur-sm">
                                        <div className="flex items-center gap-2">
                                            <FiCheck className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                                            <p className="text-[11px] font-semibold text-blue-900 leading-snug">
                                                Signature is added automatically
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>



                        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex flex-col min-h-0">
                            <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100 flex-shrink-0">
                                <h4 className="text-sm font-bold text-gray-900">Booked Dates</h4>
                                <p className="text-xs text-gray-500 mt-1">
                                    {sentDateKeys.size > 0 &&
                                        `${sentDateKeys.size} previously sent • `
                                    }
                                    Select which booked dates should be included in the email.
                                </p>
                            </div>

                            <div className="flex items-center gap-2 ml-auto mr-4 mt-2">
                                <button
                                    type="button"
                                    onClick={selectAllDates}
                                    disabled={allDatesSelected}
                                    className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-xs font-semibold hover:bg-black disabled:opacity-40"
                                >
                                    Select all
                                </button>

                                <button
                                    type="button"
                                    onClick={unselectSentDates}
                                    disabled={sentDateKeys.size === 0}
                                    className="px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold hover:bg-emerald-100 disabled:opacity-40"
                                >
                                    Unselect Sent
                                </button>

                                <button
                                    type="button"
                                    onClick={clearAllDates}
                                    disabled={selectedDateKeys.length === 0}
                                    className="px-3 py-1.5 rounded-lg bg-white border border-gray-300 text-gray-700 text-xs font-semibold hover:bg-gray-50 disabled:opacity-40"
                                >
                                    Clear all
                                </button>

                            </div>

                            <div className="p-3 sm:p-4 flex-1 overflow-y-auto bg-gray-50/60 min-h-0">
                                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-50 border-b border-gray-200">
                                            <tr>
                                                <th className="w-12 px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wide"></th>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wide">Quote Type</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wide">Child ID</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wide">Date & Time</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {bookedDates.map((row, index) => {
                                                const key = getBookedDateKey(row);
                                                const checked = selectedDateKeys.includes(key);
                                                const alreadySent = sentDateKeys.has(key);

                                                return (
                                                    <tr
                                                        key={`${row.quote_type}-${row.child_unique_id}-${index}`}
                                                        onClick={() => toggleBookedDate(row)}
                                                        className={`cursor-pointer transition ${checked ? "bg-blue-50/60" : "hover:bg-gray-50"}`}
                                                    >
                                                        <td className="px-4 py-3">
                                                            <div className={`w-5 h-5 rounded-md border flex items-center justify-center ${checked
                                                                ? "bg-blue-600 border-blue-600"
                                                                : "bg-white border-gray-300"
                                                                }`}>
                                                                {checked && <FiCheck className="w-3.5 h-3.5 text-white" />}
                                                            </div>
                                                        </td>

                                                        <td className="px-4 py-3 font-semibold text-gray-800 capitalize">
                                                            {row.quote_type}
                                                        </td>

                                                        <td className="px-4 py-3 text-gray-600 uppercase">
                                                            <div className="flex flex-col">
                                                                <span>{row.child_unique_id}</span>
                                                                {alreadySent && (
                                                                    <span className="mt-1 text-[11px] font-bold text-emerald-600">
                                                                        Sent already
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>

                                                        <td className="px-4 py-3 text-gray-800 font-medium font-mono tabular-nums whitespace-nowrap flex flex-col gap-0">
                                                            <span className="m-0 p-0">{formatBookedDate(row.date)}</span>
                                                            <span className="font-semibold font-[Poppins] text-[12px] text-[#636363] m-0 p-0">
                                                                {row?.time}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="px-4 sm:px-5 py-3 border-t border-gray-100 bg-white flex items-center gap-2 text-xs text-gray-500 flex-shrink-0">
                                {selectedBookedDates.length} of {bookedDates.length} booked date{bookedDates.length !== 1 ? "s" : ""} selected
                            </div>
                        </div>
                    </div>
                </div>

                <div className="px-4 sm:px-7 py-3 sm:py-4 border-t border-gray-100 bg-white flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 flex-shrink-0">
                    <p className="text-xs text-gray-500 hidden sm:block">
                        Files are sent securely through Notify. Private files are handled by the server.
                    </p>

                    <div className="flex items-center justify-end gap-2 w-full sm:w-auto">
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            disabled={sending}
                            className="flex-1 sm:flex-none px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50"
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            onClick={() => setPreviewOpen(true)}
                            className="flex-1 sm:flex-none px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 flex items-center justify-center gap-2"
                        >
                            <FiEye className="w-4 h-4" />
                            Preview
                        </button>

                        <button
                            type="button"
                            onClick={handleSend}
                            disabled={sending || selectedBookedDates.length <= 0}
                            className="flex-1 sm:flex-none px-5 py-2.5 text-sm font-semibold text-white bg-gray-900 rounded-xl hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
                        >
                            {sending ? (
                                <Loader className="w-4 h-4 animate-spin" />
                            ) : (
                                <FiSend className="w-4 h-4" />
                            )}
                            {sending ? "Sending..." : "Send Email"}
                        </button>
                    </div>
                </div>
            </div>


            {previewOpen && createPortal(
                <div className="mobile-email-modal fixed inset-0 z-[10001] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-5">
                    <div className="bg-white w-full sm:max-w-3xl h-[90dvh] sm:h-[82vh] rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col">
                        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">Email Preview</h3>
                                <p className="text-xs text-gray-500 mt-1">
                                    This is how the email body will look.
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={() => setPreviewOpen(false)}
                                className="w-10 h-10 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                            >
                                <FiX className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 text-xs text-gray-600 space-y-1">
                            <p><strong>To:</strong> {form.to || "Not specified"}</p>
                            {ccEmails.length > 0 && <p><strong>CC:</strong> {ccEmails.join(", ")}</p>}
                            <p><strong>Subject:</strong> {form.subject || "Not specified"}</p>
                        </div>

                        <div className="flex-1 overflow-y-auto bg-gray-100 p-4">
                            <div
                                className="mx-auto max-w-[680px]"
                                dangerouslySetInnerHTML={{ __html: buildEmailHtml() }}
                            />
                        </div>

                        <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">
                            {/* <a
                                href={contactUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 flex items-center gap-2"
                            >
                                <FiExternalLink className="w-4 h-4" />
                                Open Contact Link
                            </a> */}

                            <button
                                type="button"
                                onClick={() => setPreviewOpen(false)}
                                className="px-5 py-2.5 text-sm font-semibold text-white bg-gray-900 rounded-xl hover:bg-black"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {userPickerOpen && createPortal(
                <div className="mobile-email-modal fixed inset-0 z-[10000] bg-black/45 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-5">
                    <div className="bg-white w-full sm:max-w-xl h-[80dvh] sm:h-[620px] rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col">
                        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">Select Users</h3>
                                <p className="text-xs text-gray-500 mt-1">
                                    Selected users will be added to CC.
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={() => setUserPickerOpen(false)}
                                className="w-10 h-10 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                            >
                                <FiX className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-4 border-b border-gray-100">
                            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5">
                                <FiSearch className="w-4 h-4 text-gray-400" />
                                <input
                                    value={userSearch}
                                    onChange={e => setUserSearch(e.target.value)}
                                    placeholder="Search users..."
                                    className="bg-transparent outline-none flex-1 text-sm"
                                />
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 bg-gray-50 space-y-2">
                            {filteredUsers.map(user => {
                                const checked = pickerSelectedIds.includes(user.id);
                                const name = user.full_name || user.username || user.email;

                                return (
                                    <button
                                        key={user.id}
                                        type="button"
                                        onClick={() => toggleUserPicker(user.id)}
                                        className={`w-full flex items-center gap-3 p-3 rounded-2xl border text-left transition-all ${checked
                                            ? "bg-blue-50 border-blue-200"
                                            : "bg-white border-gray-200 hover:border-gray-300"
                                            }`}
                                    >
                                        {user.avatar_url ? (
                                            <img
                                                src={user.avatar_url}
                                                alt={name}
                                                className="w-10 h-10 rounded-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-10 h-10 rounded-full bg-gray-900 text-white flex items-center justify-center font-bold text-sm">
                                                {(name || "?")[0].toUpperCase()}
                                            </div>
                                        )}

                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-semibold text-gray-800 truncate">
                                                {name}
                                            </p>
                                            <p className="text-xs text-gray-500 truncate">
                                                {user.email}
                                            </p>
                                        </div>

                                        <div className={`w-5 h-5 rounded-md border flex items-center justify-center ${checked
                                            ? "bg-blue-600 border-blue-600"
                                            : "bg-white border-gray-300"
                                            }`}>
                                            {checked && <FiCheck className="w-3.5 h-3.5 text-white" />}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setUserPickerOpen(false)}
                                className="px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50"
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                onClick={applySelectedUsersToCc}
                                className="px-5 py-2.5 text-sm font-semibold text-white bg-gray-900 rounded-xl hover:bg-black"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    ) : null;

    return (
        <>
            <CancelledAction>
                <button
                    type="button"
                    onClick={openModal}
                    disabled={bookedDates?.length <= 0}
                    className="flex items-center gap-2 px-3 py-[0.4rem] bg-white border border-gray-300 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <FiMail className="w-4 h-4" />
                    {bookedDates?.length <= 0 ? "No Dates to Send" : "Send Dates to Customer"}
                </button>
            </CancelledAction>
            {open && createPortal(modal, document.body)}
        </>
    );
};

export default JobBookedDatesEmailSender;